import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingButtons from '@/components/feature/FloatingButtons';
import MenuHero from './components/MenuHero';
import MenuCategoryNav from './components/MenuCategoryNav';
import CategorySection from './components/CategorySection';
import StickyMobileBar from './components/StickyMobileBar';
import MenuNote from './components/MenuNote';
import { db } from '@/lib/firebase';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';

export default function MenuPage() {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Menu | Bucara Burger – Colombian Food Truck Boise Idaho';

    // Fetch Categories
    const qCat = query(collection(db, 'categories'));
    const unsubCat = onSnapshot(qCat, (snap) => {
      setCategories(snap.docs.map(doc => {
        const data = doc.data();
        const name = data.name ? (typeof data.name === 'string' ? data.name : (data.name[i18n.language] || data.name.en || '')) : (data.label || data.title || '');
        return { id: doc.id, ...data, label: name, name: name };
      }));
    });

    // Fetch Menu Items
    const qItems = query(collection(db, 'menu_items'));
    const unsubItems = onSnapshot(qItems, (snap) => {
      setItems(snap.docs.map(doc => {
        const data = doc.data();
        const name = data.name ? (typeof data.name === 'string' ? data.name : (data.name[i18n.language] || data.name.en || '')) : '';
        const description = data.description ? (typeof data.description === 'string' ? data.description : (data.description[i18n.language] || data.description.en || '')) : '';
        return { id: doc.id, ...data, name, description };
      }));
      setLoading(false);
    });

    return () => {
      unsubCat();
      unsubItems();
    };
  }, [i18n.language]);

  const getItemsByCategory = (categoryId: string) =>
    items.filter((item) => item.categoryId === categoryId);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="text-[#F57F25] animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* SEO structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Menu',
            name: 'Bucara Burger Menu',
            description: 'Colombian food truck menu featuring gourmet burgers, Colombian hot dogs, empanadas, arepas, and natural juices in Boise Idaho.',
            url: 'https://bucaraburger.com/menu',
            hasMenuSection: categories.map((cat) => ({
              '@type': 'MenuSection',
              name: cat.label,
              hasMenuItem: getItemsByCategory(cat.id).map((item) => ({
                '@type': 'MenuItem',
                name: item.name,
                description: item.description,
                offers: {
                  '@type': 'Offer',
                  price: typeof item.price === 'string' ? item.price.replace('$', '') : item.price,
                  priceCurrency: 'USD',
                },
              })),
            })),
          }),
        }}
      />

      <Navbar />
      <MenuHero />
      <MenuCategoryNav categories={categories} />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-12 pb-28 md:pb-12">
        <MenuNote />

        {categories.map((category) => {
          const catItems = getItemsByCategory(category.id);
          if (catItems.length === 0) return null;
          
          return (
            <CategorySection
              key={category.id}
              category={category}
              items={catItems}
            />
          );
        })}

        {/* Bottom CTA */}
        <section className="mt-8 relative rounded-2xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Colombian%20food%20truck%20night%20scene%20urban%20street%20food%20Boise%20Idaho%20dark%20moody%20atmosphere%20orange%20lights%20grill%20smoke%20burgers%20hot%20dogs%20Latin%20food%20truck%20exterior%20vibrant%20energy&width=1200&height=400&seq=mctabottom&orientation=landscape"
            alt="Order from Bucara Burger"
            className="w-full h-56 md:h-64 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40 flex items-center">
            <div className="px-8 md:px-12">
              <p className="text-[#F57F25] text-xs font-bold uppercase tracking-widest mb-2">{t('menu_cta_eyebrow')}</p>
              <h3 className="text-white font-black text-2xl md:text-3xl uppercase mb-6 leading-tight">
                {t('menu_cta_title')}<br />{t('menu_cta_title_2')}
              </h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://wa.me/19862173604?text=Hola%2C%20quiero%20hacer%20un%20pedido.%20%2F%20Hello%2C%20I%20want%20to%20place%20an%20order."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#F57F25] hover:bg-[#e06d15] text-black font-bold text-sm px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-base"></i>
                  </div>
                  {t('btn_order_whatsapp')}
                </a>
                <a
                  href="tel:+19862173604"
                  className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold text-sm px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-base"></i>
                  </div>
                  {t('btn_call_now')}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingButtons />
      <StickyMobileBar />
    </div>
  );
}
